import EmptyState from "@/components/EmptyState";
import { RowHeader, TableCardRow, TableCardMobileStack, TABLE_CARD_BODY_CELL } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import { formatDate } from "@/pages/_layouts/jobs/_utils";
import { useNavigate } from "@/router";
import CareerPlanSummaryCard from "./_CareerPlanSummaryCard";
import { UNTITLED_CAREER_PLAN_TITLE } from "./_CareerPlanTitle";
import {
  groupHubCareerPlans,
  HUB_CAREER_PLAN_COMPLETED_LABEL,
  HUB_CAREER_PLAN_DRAFTS_LABEL,
  HUB_CAREER_PLAN_PUBLISHED_LABEL,
  type HubCareerPlan
} from "./careerPlanHubSummaries";

const COLUMN_COUNT = 2;
const DETAILS_CARD_TABLE_PADDING =
  "[&_tbody_td]:px-2 [&_tbody_td:first-child]:pl-6 [&_tbody_td:last-child]:pr-6 [&_tbody_td:only-child]:px-6";

function planDisplayTitle(title: string | null) {
  return title?.trim() || UNTITLED_CAREER_PLAN_TITLE;
}

function CareerPlanListRow({ plan, pgaId }: { plan: HubCareerPlan; pgaId?: string }) {
  const navigate = useNavigate();
  const title = planDisplayTitle(plan.title);
  const lastModified = plan.updatedAt ? formatDate(plan.updatedAt) : "";

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`career-plan-row-${plan.id}`}
      onClick={
        pgaId
          ? () =>
              navigate("/people/:pgaId/career/career-profile/plans/:planId", {
                params: { pgaId, planId: plan.id }
              })
          : undefined
      }
      ariaLabel={`Open ${title}`}
      mobile={<TableCardMobileStack lines={[title, lastModified || renderEmptyCellState(null)]} />}
    >
      <td className={TABLE_CARD_BODY_CELL}>{title}</td>
      <td className={`${TABLE_CARD_BODY_CELL} whitespace-nowrap text-sys-brand-on-surface-variant`}>
        {lastModified || renderEmptyCellState(null)}
      </td>
    </TableCardRow>
  );
}

function CareerPlanGroup({
  label,
  plans,
  pgaId,
  rowTestId
}: {
  label: string;
  plans: HubCareerPlan[];
  pgaId?: string;
  rowTestId: string;
}) {
  if (plans.length === 0) return null;

  return (
    <>
      <RowHeader label={label} columnCount={COLUMN_COUNT} rowTestId={rowTestId} />
      {plans.map((plan) => (
        <CareerPlanListRow key={plan.id} plan={plan} pgaId={pgaId} />
      ))}
    </>
  );
}

export default function CareerPlanCard({
  loading,
  pgaId,
  plans = [],
  canCreatePlan = false
}: {
  loading: boolean;
  pgaId?: string;
  plans?: HubCareerPlan[];
  canCreatePlan?: boolean;
}) {
  const navigate = useNavigate();
  const { drafts, published, completed } = groupHubCareerPlans(plans);
  const hasPlans = plans.length > 0;
  const createAction =
    canCreatePlan && pgaId
      ? {
          children: "Create Career Plan",
          icon: "add" as const,
          to: "/people/:pgaId/career/career-profile/onboarding" as const,
          params: { pgaId }
        }
      : undefined;

  const showCreateFooter = Boolean(canCreatePlan && pgaId);

  return (
    <CareerPlanSummaryCard
      title="Career Plans"
      testId="career-plan"
      loading={loading}
      showViewAll={false}
      footer={
        canCreatePlan && pgaId && hasPlans
          ? {
              label: "+ Create Plan",
              footerTestId: "career-plan-create-footer",
              buttonTestId: "career-plan-create-plan",
              onClick: () =>
                navigate("/people/:pgaId/career/career-profile/onboarding", {
                  params: { pgaId }
                })
            }
          : undefined
      }
    >
      {hasPlans ? (
        <div className={`${DETAILS_CARD_TABLE_PADDING} -mx-6 min-w-0 ${showCreateFooter ? "" : "-mb-6"}`}>
          <table className="w-full" data-testid="career-plan-table">
            <colgroup>
              <col />
              <col className="w-28" />
            </colgroup>
            <tbody>
              <CareerPlanGroup
                label={HUB_CAREER_PLAN_DRAFTS_LABEL}
                plans={drafts}
                pgaId={pgaId}
                rowTestId="career-plan-drafts-header"
              />
              <CareerPlanGroup
                label={HUB_CAREER_PLAN_PUBLISHED_LABEL}
                plans={published}
                pgaId={pgaId}
                rowTestId="career-plan-published-header"
              />
              <CareerPlanGroup
                label={HUB_CAREER_PLAN_COMPLETED_LABEL}
                plans={completed}
                pgaId={pgaId}
                rowTestId="career-plan-completed-header"
              />
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center">
          <EmptyState
            iconName="rubric"
            title="No Career Plans"
            caption="This person has not been provided a career plan yet."
            action={createAction}
          />
        </div>
      )}
    </CareerPlanSummaryCard>
  );
}
